/**
 * Supabase client wiring.
 *
 * Lazy — never crashes when env vars are missing (keeps Phase 1/2 usable
 * without secrets). Phase 4 adds full OAuth + session handoff to/from main
 * Jubit (postMessage bridge in `./jubitOAuth.ts`).
 *
 * Sprint 30 — switched the underlying client construction to
 * `@jubit-ai/auth-bridge` so jubuddy shares the EXACT auth setup that
 * powers www.jubit.ai. With `storageMode: 'auto'` + `cookieDomain:
 * '.jubit.ai'`:
 *
 *   - On jubuddy.com (legacy domain) → falls back to localStorage,
 *     identical to the previous behaviour.
 *   - On play.jubit.ai (post-DNS-pivot) → uses cookie storage scoped
 *     to .jubit.ai, sharing the session natively with www.jubit.ai.
 *
 * Env vars to set in Vercel project → Environment Variables:
 *   VITE_SUPABASE_URL=https://uqtkxmlpoenphznnapsv.supabase.co  (jubit project)
 *   VITE_SUPABASE_ANON_KEY=<jubit anon key>
 */

import { createAuthBridgeClient } from '@jubit-ai/auth-bridge';
import type { SupabaseClient } from '@supabase/supabase-js';

const URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

let _client: SupabaseClient | null = null;

export function getSupabase(): SupabaseClient | null {
  if (!URL || !KEY) return null;
  if (!_client) {
    _client = createAuthBridgeClient({
      supabaseUrl: URL,
      supabaseAnonKey: KEY,
      storageMode: 'auto',
      cookieDomain: '.jubit.ai',
      storageKey: 'sb-uqtkxmlpoenphznnapsv-auth-token',
    }) as SupabaseClient;
  }
  return _client;
}

/** True when env vars are configured and Supabase is usable. */
export function hasSupabaseConfig(): boolean {
  return Boolean(URL && KEY);
}
